import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card } from 'react-bootstrap';
import useApplicationStore from '../../store/applicationStore';
import './Application.css';
import usePageTitle from '../../hooks/UsePageTitle';

const ThankYouPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { leadData, documentData, bankDetails, personalDetails } = useApplicationStore();
  usePageTitle("Personal Loan Application Initiated - Snapfunds");
  const [applicationDetails, setApplicationDetails] = useState({
    applicationId: '',
    lenderName: '',
    installment: '',
    loanAmount: '',
    status: 'In Progress',
    casheLoginUrl: null
  });

  const [isLoading, setIsLoading] = useState(true);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    console.log('ThankYouPage mounted');
    console.log('Location state:', location.state);
    console.log('Lead data:', leadData);
    
    // Only allow access if we have location.state (from current application submission)
    // Don't rely on store leadData as it might be old data
    if (!location.state) {
      console.log('No location state available, redirecting to homepage');
      navigate('/');
      return;
    }
    
    // Validate timestamp to ensure data is fresh (within last 5 minutes)
    const now = Date.now();
    const submittedAt = location.state.submittedAt;
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
    
    if (submittedAt && (now - submittedAt) > fiveMinutes) {
      console.log('Application data is too old, redirecting to homepage');
      navigate('/');
      return;
    }
    
    // Use only location.state for application details (fresh data from current submission)
    console.log('Using location state for application details');
    setApplicationDetails(location.state);
    
    // Set loading to false once we have data
    setIsLoading(false);
  }, [location.state, navigate]);

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown <= 1) {
          clearInterval(timer);
          // Open Cashe login URL when countdown reaches 0
          if (applicationDetails.casheLoginUrl) {
            console.log('Countdown reached 0, opening Cashe login URL:', applicationDetails.casheLoginUrl);
            window.open(applicationDetails.casheLoginUrl, '_blank');
          } else {
            console.log('No Cashe login URL available, redirecting to homepage');
            // Clear the application store when redirecting to prevent old data issues
            useApplicationStore.getState().resetApplication();
            navigate('/');
          }
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      // Clear application data when component unmounts to prevent old data issues
      useApplicationStore.getState().resetApplication();
    };
  }, [navigate, applicationDetails.casheLoginUrl]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleOpenCasheLogin = () => {
    if (applicationDetails.casheLoginUrl) {
      console.log('Manual click - opening URL:', applicationDetails.casheLoginUrl);
      // Open cashe login URL in new tab (same method as TrustPaisa)
      window.open(applicationDetails.casheLoginUrl, '_blank');
    }
  };

  // Show loading screen while checking for leadData
  if (isLoading) {
    return (
      <div className="thank-you-page-fullscreen">
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg={10} md={12} sm={12}>
              <div className="thank-you-container text-center">
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#28a745' }}></i>
                  <p className="mt-3" style={{ fontSize: '1.2rem', color: '#333' }}>Loading...</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="thank-you-page-fullscreen">
      <Container fluid>
        <Row className="justify-content-center">
          <Col lg={10} md={12} sm={12}>
            <div className="thank-you-container">

              {/* Thank You Message */}
              <div className="text-center mb-5">
                <h1 className="thank-you-title">Thank You!</h1>
                <p className="thank-you-subtitle">
                  Your loan application has been successfully initiated.
                </p>
                <p className="thank-you-description">
                 You can complete your application by visiting our lending partner's website.{' '}<br />
                 {applicationDetails.casheLoginUrl && (
                   <>
                     <a 
                       href={applicationDetails.casheLoginUrl} 
                       target="_blank" 
                       rel="noopener noreferrer"
                       style={{ 
                         color: '#28a745', 
                         textDecoration: 'none',
                         fontWeight: '600',
                         borderBottom: '2px solid #28a745',
                         paddingBottom: '1px'
                       }}
                       onMouseEnter={(e) => {
                         e.target.style.textDecoration = 'underline';
                       }}
                       onMouseLeave={(e) => {
                         e.target.style.textDecoration = 'none';
                       }}
                     >
                       Click here to continue 
                     </a>
                     <br />
                     <span style={{ 
                       fontSize: '0.9rem', 
                       color: '#666', 
                       fontWeight: '500',
                       marginTop: '8px',
                       display: 'inline-block'
                     }}>
                       {countdown > 0 ? (
                         <>You will be automatically redirected in <span style={{ 
                           color: '#28a745', 
                           fontWeight: '700' 
                         }}>{countdown}</span> seconds</>
                       ) : (
                         <>Redirecting now... If the page doesn't open, <button 
                           onClick={handleOpenCasheLogin}
                           style={{
                             background: 'none',
                             border: 'none',
                             color: '#28a745',
                             textDecoration: 'underline',
                             cursor: 'pointer',
                             fontSize: 'inherit',
                             fontWeight: '600'
                           }}
                         >
                           click here
                         </button></>
                       )}
                     </span>
                   </>
                 )}
                </p>
              </div>

              {/* Application Details Card */}
              <Card className="application-details-card mb-4">
                <Card.Header className="application-details-header">
                  <h3 className="mb-0">Application Details</h3>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6} sm={12} className="mb-3">
                      <div className="detail-item">
                        <label className="detail-label">Application ID</label>
                        <div className="detail-value">{applicationDetails.applicationId}</div>
                      </div>
                    </Col>
                    <Col md={6} sm={12} className="mb-3">
                      <div className="detail-item">
                        <label className="detail-label">Lending Partner</label>
                        <div className="detail-value">{applicationDetails.lendingPartner}</div>
                      </div>
                    </Col>
                    <Col md={6} sm={12} className="mb-3">
                      <div className="detail-item">
                        <label className="detail-label">Status</label>
                        <div className="detail-value status-submitted">
                          <i className="fas fa-clock me-2"></i>
                          {applicationDetails.status}
                        </div>
                      </div>
                    </Col>
                    <Col md={6} sm={12} className="mb-3">
                      <div className="detail-item">
                        <label className="detail-label">Initiated On</label>
                        <div className="detail-value">{new Date().toLocaleDateString('en-IN')}</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Next Steps
              <Card className="next-steps-card mb-4">
                <Card.Header className="next-steps-header">
                  <h3 className="mb-0">What's Next?</h3>
                </Card.Header>
                <Card.Body>
                  <div className="next-steps-list">
                    <div className="next-step-item">
                      <div className="step-icon">
                        <i className="fas fa-search"></i>
                      </div>
                      <div className="step-content">
                        <h5>Application Review</h5>
                        <p>Our team will review your application and documents within 24-48 hours.</p>
                      </div>
                    </div>
                    <div className="next-step-item">
                      <div className="step-icon">
                        <i className="fas fa-phone"></i>
                      </div>
                      <div className="step-content">
                        <h5>Verification Call</h5>
                        <p>We may contact you for additional verification if required.</p>
                      </div>
                    </div>
                    <div className="next-step-item">
                      <div className="step-icon">
                        <i className="fas fa-envelope"></i>
                      </div>
                      <div className="step-content">
                        <h5>Status Update</h5>
                        <p>You will receive an email/SMS with your application status and next steps.</p>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card> */}

              {/* Action Buttons */}
              {/* <div className="action-buttons text-center">
                {/* Countdown Timer */}
                {/* <div className="countdown-timer mb-4">
                  <p className="countdown-text">
                    Redirecting to homepage in <span className="countdown-number">{countdown}</span> seconds
                  </p>
                </div> */}
                
                {/* Commented out button - keeping code for future use */}
                {/* <Button 
                  variant="primary" 
                  className="mb-3"
                  onClick={handleGoHome}
                >
                  <i className="fas fa-home me-2"></i>
                  Back to Homepage
                </Button> */}
              {/* </div> */}

              {/* Contact Information */}
              {/* <div className="contact-info text-center mt-4">
                <p className="contact-text">
                  Need help? Contact us at{' '}
                  <a href="tel:+919662267474" className="contact-link">+91 9662267474</a>
                  {' '}or{' '}
                  <a href="mailto:support@snapfunds.com" className="contact-link">support@snapfunds.com</a>
                </p>
              </div> */}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ThankYouPage;
